import { memo, useCallback } from "react";

import type { CancelPolicyItemInput, InputMaybe } from "@/lib/gql/__generated__/graphql";

import { externalLink } from "@/routes/_constants/external-link";

import { FeatureIslandCategoryQuestionForMain } from "@/components/features/_layouts/island/main-with-category/components/category-main-question";
import type { ForArrayType } from "@/components/features/_model/types/auto-generator-for-view-model";

import { FormItem } from "@/components/ui/form/_base/form";
import type { FormOptionType } from "@/components/ui/form/_types/type";
import { CheckboxField } from "@/components/ui/form/fields/check-box-field/check-box";
import { SelectField } from "@/components/ui/form/fields/select-field";

import { QuestionSectionWrapper } from "../../../system/question/_layouts/item-wrapper";
import { QuestionCardItem } from "../../../system/question/card/card-item";
import { QuestionCardItemsWrapper } from "../../../system/question/card/card-items-wrapper";

type Props = {
	cancelPolicy: ForArrayType<InputMaybe<CancelPolicyItemInput[]> | undefined> | undefined;

	useBaseCancelPolicy?: boolean;
	toggleUseBaseCancelPolicy?: () => void;

	appendCancelPolicy: () => void;
	removeCancelPolicy: (index: number) => void;

	cancelPolicyBeforeDaysOptions: (index: number) => FormOptionType[];
	changeCancelPolicyBeforeDays: (value: number, index: number) => void;

	cancelPolicyChargeRatioOptions: FormOptionType[];
	changeCancelPolicyChargeRatio: (value: number, index: number) => void;

	isEdited: boolean | undefined;
	hasError: boolean | undefined;

	guide?: string;
};

/**
 * CHILLNN サポート「50日以上のキャンセルポリシーを設定される場合について」
 * https://support.chillnn.com/faq/host/policy/3cafffe15c5781bdab44e9e280cd7eec
 *
 * ベースURLは externalLink.faqSite（dev/prd で自動切替）を使う。
 * パスは Notion ページ ID で構成されるため、日本語タイトルや旧スラッグは使わない。
 */
const CANCEL_POLICY_EXTENSION_NOTICE_URL = `${externalLink.faqSite}/faq/host/policy/3cafffe15c5781bdab44e9e280cd7eec`;

export const QuestionCancelPolicy = memo(
	({
		cancelPolicy,
		useBaseCancelPolicy = false,
		toggleUseBaseCancelPolicy,
		appendCancelPolicy,
		removeCancelPolicy,
		cancelPolicyBeforeDaysOptions,
		changeCancelPolicyBeforeDays,
		cancelPolicyChargeRatioOptions,
		changeCancelPolicyChargeRatio,
		isEdited,
		guide,
	}: Props) => {
		const canUseBaseCancelPolicy = !!toggleUseBaseCancelPolicy;

		const append = useCallback(() => {
			appendCancelPolicy();
		}, [ appendCancelPolicy ]);

		const remove = useCallback(
			(index: number) => () => {
				removeCancelPolicy(index);
			},
			[ removeCancelPolicy ],
		);

		const handleBeforeDaysSelect = useCallback(
			(index: number) => {
				return (input: string) => {
					changeCancelPolicyBeforeDays(Number(input), index);
				};
			},
			[ changeCancelPolicyBeforeDays ],
		);

		const handleRatioSelect = useCallback(
			(index: number) => {
				return (input: string) => {
					changeCancelPolicyChargeRatio(Number(input), index);
				};
			},
			[ changeCancelPolicyChargeRatio ],
		);

		return (
			<FeatureIslandCategoryQuestionForMain
				required={false}
				title={"キャンセルポリシー"}
				isEdited={isEdited ?? false}
				hasError={cancelPolicy?.hasError ?? false}
				guide={guide}
				inputChildren={
					<div className="space-y-6">
						<p className="text-error-600">
							最大90日までのキャンセルポリシーを設定できるようになりました。
							50日以上を設定される場合は必ず
							<a
								href={CANCEL_POLICY_EXTENSION_NOTICE_URL}
								target="_blank"
								rel="noopener noreferrer"
								className="font-bold underline underline-offset-4"
							>
								こちら
							</a>
							をご確認ください
						</p>
						{canUseBaseCancelPolicy && (
							<div>
								<CheckboxField
									value={useBaseCancelPolicy ?? true}
									label={"ホテルポリシーで設定したキャンセルポリシーに従う"}
									handleChange={toggleUseBaseCancelPolicy}
								/>
							</div>
						)}

						{!useBaseCancelPolicy && (
							<QuestionCardItemsWrapper appendItem={append}>
								<FormItem>
									{cancelPolicy?.value?.map((item, index) => {
										return (
											<QuestionCardItem
												key={`cancel_policy_${item.beforeDays.value + item.chargeRatio.value}_${index}`}
												removeItem={remove(index)}
											>
												<div className="flex flex-col gap-y-10">
													<QuestionSectionWrapper>
														<SelectField
															value={item.beforeDays.value}
															options={cancelPolicyBeforeDaysOptions(index)}
															handleValueChange={handleBeforeDaysSelect(index)}
															placeholder="日前のキャンセルから"
															errorMessage={item.beforeDays.error}
														/>
														<p>日前のキャンセルから</p>
													</QuestionSectionWrapper>
													<QuestionSectionWrapper>
														<SelectField
															value={item.chargeRatio.value}
															options={cancelPolicyChargeRatioOptions}
															handleValueChange={handleRatioSelect(index)}
															placeholder="のキャンセル料金"
															errorMessage={item.chargeRatio.error}
														/>
														<p>のキャンセル料金</p>
													</QuestionSectionWrapper>
												</div>
											</QuestionCardItem>
										);
									})}
								</FormItem>
							</QuestionCardItemsWrapper>
						)}
					</div>
				}
			/>
		);
	},
);

QuestionCancelPolicy.displayName = "QuestionCancelPolicy";
